import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Download, TrendingUp, Filter } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { gbp, ukDate, typeBadge } from "./crmPipelineShared";

export default function CrmConversions() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ month: "", source: "", contact_type: "" });

  const loadTable = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/crm/conversions", { params }).then((r) => setRows(r.data.conversions)).catch(() => toast.error("Could not load conversions"));
  }, [filters]);

  useEffect(() => { api.get("/crm/conversion-analytics").then((r) => setAnalytics(r.data)).catch(() => {}); }, []);
  useEffect(loadTable, [loadTable]);

  const months = (analytics?.by_month || []).map((m) => m.key);
  const srcs = (analytics?.by_source || []).map((s) => s.key);

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/crm/conversions/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "emerald-crown-conversions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const STAT = [["Deals Completed", analytics?.total_completed, false], ["Fee Revenue", analytics?.total_fee_revenue, true],
    ["Fee This Month", analytics?.fee_this_month, true], ["Total Deal Value", analytics?.total_deal_value, true]];

  return (
    <CrmShell title="Conversions">
      <div className="flex items-center justify-between gap-2 mb-5 flex-wrap">
        <div className="flex items-center gap-2 text-emerald-100/70 text-sm"><TrendingUp className="w-4 h-4 text-brass" /> Completed deals &amp; revenue</div>
        <button onClick={exportCsv} data-testid="conv-export-csv" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-5 py-2.5 text-sm shadow-lg"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {STAT.map(([l, v, money]) => (
          <div key={l} className="bg-[#0F2E23] rounded-xl border border-white/10 p-4">
            <div className="text-xs text-emerald-100/60">{l}</div>
            <div className="mt-2 text-2xl font-mono font-semibold text-white">{analytics ? (money ? gbp(v) : (v ?? 0)) : "—"}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <span className="text-xs text-emerald-100/50 flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Filter:</span>
        <select value={filters.month} onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))} data-testid="conv-filter-month" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All months</option>{months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} data-testid="conv-filter-source" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All sources</option>{srcs.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.contact_type} onChange={(e) => setFilters((f) => ({ ...f, contact_type: e.target.value }))} data-testid="conv-filter-type" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All types</option>{["Vendor", "Investor", "Both", "Professional / Introducer"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-x-auto" data-testid="conv-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-emerald-100/50 border-b border-white/10">
            <th className="py-2.5 px-3">Date</th><th className="py-2.5 px-3">Name</th><th className="py-2.5 px-3">Type</th>
            <th className="py-2.5 px-3 hidden sm:table-cell">Source</th><th className="py-2.5 px-3 hidden md:table-cell">Transaction</th>
            <th className="py-2.5 px-3 text-right">Deal value</th><th className="py-2.5 px-3 text-right">EC fee</th>
          </tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} onClick={() => navigate(`/crm/contact/${c.store}/${c.contact_id}`)} data-testid={`conv-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
              <td className="py-3 px-3 text-emerald-100/70 text-xs">{ukDate(c.completion_date)}</td>
              <td className="py-3 px-3 text-white font-medium">{c.name}</td>
              <td className="py-3 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${typeBadge(c.contact_type)}`}>{c.contact_type}</span></td>
              <td className="py-3 px-3 hidden sm:table-cell text-emerald-100/60 text-xs">{c.source}</td>
              <td className="py-3 px-3 hidden md:table-cell text-emerald-100/60 text-xs">{c.transaction_type}</td>
              <td className="py-3 px-3 text-right text-white font-mono">{gbp(c.price)}</td>
              <td className="py-3 px-3 text-right text-emerald-300 font-mono">{gbp(c.fee)}</td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <p className="text-emerald-100/50 text-sm py-8 text-center">No completed deals match.</p>}
      </div>
    </CrmShell>
  );
}
